import { useCallback, useEffect } from 'react'
import Link from 'next/link'
import { QueryClient, dehydrate } from 'react-query'
import dayjs from 'dayjs'
import localizedFormat from 'dayjs/plugin/localizedFormat'
import Meta from 'components/Meta'
import Layout from 'components/Layout'
import Loading from 'components/Loading'
import Alert from 'components/Alert'
import GridListToggle from 'components/GridListToggle'
import ResultsList from 'components/ResultsList'
import ResultsGrid from 'components/ResultsGrid'
import ManageEmptyState from 'components/address/ManageEmptyState'
import WalletThumbnail from 'components/WalletThumbnail'
import UserThumbnail from 'components/UserThumbnail'
import SalesTable from 'components/SalesTable'
import usePush from 'hooks/usePush'
import useAddresses from 'api/hooks/useAddresses'
import useAnalytics from 'api/hooks/useAnalytics'
import { getByAddresses } from 'api/hooks/useAddresses'
import useSearch from 'api/hooks/useSearch'
import { useAddressViewStore } from 'store/state/addressView'
import { classNames, isValidAlgoAddress } from 'helpers/utilities'
import type { GetServerSideProps } from 'next'

dayjs.extend(localizedFormat)

export type AddressFilter = 'for-sale' | 'owned'
export type AddressView = AddressFilter | 'offers' | 'sales'

interface AddressProps {
  pathname: string
  view: AddressView
  address: string
}

export default function AddressPage({ pathname, view, address }: AddressProps) {
  const viewSetting = useAddressViewStore((state) => state.view)
  const setViewSetting = useAddressViewStore((state) => state.setView)

  const { data, isLoading, isSuccess, error } = useSearch({
    params: {
      owner: address,
      view: 'full'
    }
  })

  const salesQuery = useAnalytics({
    params: {
      event: ['sold'],
      sort: 'timeDesc',
      seller: address
    }
  })

  const salesUsers = salesQuery.data?.results?.map((sale) => sale.data?.buyer) || []

  const usersQuery = useAddresses({
    params: {
      address: [...new Set([...salesUsers])] as string[],
      view: 'thumbnail'
    },
    options: {
      enabled: salesUsers.length > 0
    }
  })

  const filteredResults = useCallback(
    (filter: AddressView) => {
      if (!data || !data.length) {
        return []
      }

      switch (filter) {
        case 'owned':
          return data.filter((nfd) => nfd.state === 'owned')
        case 'for-sale':
          return data.filter((nfd) => nfd.state === 'forSale' && nfd.reservedFor !== address)
        default:
          return []
      }
    },
    [data, address]
  )

  const push = usePush()

  useEffect(() => {
    if (isSuccess && ['for-sale'].includes(view)) {
      const results = filteredResults(view)

      if (results.length === 0) {
        push('/address')
      }
    }
  }, [filteredResults, isSuccess, push, view])

  const renderTabs = () => {
    const tabs = [
      {
        name: 'Owned',
        id: 'owned',
        href: `/address/${address}`,
        count: filteredResults('owned').length,
        current: view === 'owned',
        isDisabled: !filteredResults('owned').length
      },
      {
        name: 'For Sale',
        id: 'for-sale',
        href: `/address/${address}?view=for-sale`,
        count: filteredResults('for-sale').length,
        current: view === 'for-sale',
        isDisabled: filteredResults('for-sale').length === 0
      },
      {
        name: 'Sales',
        id: 'sales',
        href: `/address/${address}?view=sales`,
        count: salesQuery.data?.total,
        current: view === 'sales',
        isDisabled: false
      }
    ]

    return (
      <div className="bg-white border-b border-gray-200 sm:px-6 lg:px-8 sticky top-16 z-10">
        <div className="px-4 sm:px-0">
          <div className="flex justify-between">
            <nav className="-mb-px flex space-x-8" aria-label="Tabs">
              {tabs.map((tab) =>
                !tab.isDisabled ? (
                  <Link key={tab.id} href={tab.href} passHref>
                    <a
                      className={classNames(
                        tab.current
                          ? 'border-brand-500 text-brand-600'
                          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-200',
                        'whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm'
                      )}
                    >
                      {tab.name}
                      {tab.count ? (
                        <span
                          className={classNames(
                            tab.current
                              ? 'bg-brand-100 text-brand-600'
                              : 'bg-gray-100 text-gray-900',
                            'hidden ml-2 py-0.5 px-2.5 rounded-full text-xs font-medium md:inline-block'
                          )}
                        >
                          {tab.count}
                        </span>
                      ) : null}
                    </a>
                  </Link>
                ) : (
                  <span
                    key={tab.name}
                    className="whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm border-transparent text-gray-500 opacity-50 pointer-events-none"
                  >
                    {tab.name}
                  </span>
                )
              )}
            </nav>
            <div
              className={classNames(
                ['offers', 'sales'].includes(view) ? 'hidden' : 'hidden md:flex items-center'
              )}
            >
              <GridListToggle onChange={setViewSetting} value={viewSetting} />
            </div>
          </div>
        </div>
      </div>
    )
  }

  const renderResults = () => {
    const results = filteredResults(view)

    if (viewSetting === 'list') {
      return <ResultsList results={results} walletAddress={address} />
    }

    return (
      <div>
        <div className="md:hidden">
          <ResultsList results={results} walletAddress={address} />
        </div>
        <div className="hidden md:block">
          <ResultsGrid results={results} walletAddress={address} />
        </div>
      </div>
    )
  }

  const renderPage = () => {
    if (view === 'sales') {
      return (
        <SalesTable
          title="Sales"
          description="List of sales made by this address"
          sales={salesQuery.data?.results}
          totalSales={salesQuery.data?.total}
          sellers={usersQuery.data}
          isLoading={salesQuery.isLoading}
          error={salesQuery.error}
        />
      )
    }

    if (filteredResults(view).length === 0) {
      return <ManageEmptyState view={view} />
    }

    return <div className="sm:px-6 lg:px-8 pt-8">{renderResults()}</div>
  }

  const renderContent = () => {
    if (isLoading) {
      return (
        <div className="flex flex-col items-center justify-center py-32">
          <Loading />
        </div>
      )
    }

    if (error) {
      return (
        <div className="px-6 py-5">
          <Alert type="error" title={`Error fetching NFDs for "${address}"`} error={error} />
        </div>
      )
    }

    return (
      <div className="bg-gray-50 flex-1">
        <header className="bg-white">
          <div className="py-8 sm:py-12 md:py-16 px-4 sm:px-6 lg:px-8 xl:flex xl:items-center xl:justify-between">
            <div className="flex-1 min-w-0">
              <h1 className="text-2xl sm:text-3xl md:text-4xl font-extrabold tracking-tight text-gray-900">
                Account
              </h1>
              <div className="flex align-middle">
                <div className="flex flex-col sm:flex sm:flex-row mt-2">
                  <div className="flex sm:pr-3">
                    <WalletThumbnail address={address} className="text-gray-500" />
                  </div>
                  <div className="flex mt-2 sm:mt-0">
                    <UserThumbnail
                      className="border-l-2 sm:pl-3"
                      address={address}
                      showAddressFallback={false}
                    />
                  </div>
                </div>
                <div className="flex"></div>
              </div>
              <div></div>
            </div>
          </div>
        </header>
        {renderTabs()}
        <main className="pb-16">{renderPage()}</main>
      </div>
    )
  }

  return (
    <>
      <Meta title={`Account ${address}`} pathname={pathname} />
      <Layout pathname={pathname}>{renderContent()}</Layout>
    </>
  )
}

export const getServerSideProps: GetServerSideProps = async (context) => {
  const address = context.params?.address as string
  const isAddressValid = isValidAlgoAddress(address)

  if (!isAddressValid) {
    return {
      redirect: {
        destination: '/',
        permanent: false
      }
    }
  }

  const view = context.query?.view || 'owned'

  const queryClient = new QueryClient()
  await queryClient.prefetchQuery(['search', { address: [address] }], () =>
    getByAddresses({ address: [address] }, true, {
      validateStatus: (status: number) => (status >= 200 && status < 300) || status === 404
    })
  )

  return {
    props: {
      pathname: context.resolvedUrl,
      address,
      view,
      dehydratedState: dehydrate(queryClient)
    }
  }
}
